import type { ApolloClient } from '@apollo/client';
import { Sentiments } from '@sede-enterprise/shell-ds-fabrics';
import {
  Divider,
  Flexbox,
  FormField,
  Loading,
  SingleDatePicker,
  Sizes,
  TextArea,
  Toggle,
  Variants,
} from '@sede-enterprise/shell-ds-react-framework';
import { Action } from '@sede-enterprise/shell-ds-react-framework/build/esm/react-base/src/components/Modal/Modal.types';
import { SiteAvailabilityControl } from '@see-power-pft-common-js/net-availability/enums/site-availability-control';
import { getSourceKey } from '@see-power-pft-common-js/utils/sourceKey';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import debounce from 'lodash/debounce';
import {
  AssetInputsErrors,
  assetLevelFormValidationSchema,
  AssetOperationalLimits,
} from 'pages/Availability/AssetLevel/assetFormValidation';
import { AssetLimitsTable } from 'pages/Availability/AssetLevel/AssetLimitsTable';
import { AssetSelector } from 'pages/Availability/AssetLevel/AssetSelector';
import { getPreviewChartTime } from 'pages/Availability/common/AvailabilityChart/utils/chartDataUtils';
import {
  performEditAssetAvailabilityMutation,
  performNewAssetAvailabilityMutation,
} from 'pages/Availability/common/availabilitySubmitHandlers';
import { DateTimeSPPicker } from 'pages/Availability/common/DateTimeSPPicker';
import { ASSET_FORM_INITIAL_VALUES } from 'pages/Availability/common/FormDrawer/constants';
import {
  NewAssetAvailabilityEntry,
  NewSiteAvailabilityEntry,
  PowerEnergyInput,
} from 'pages/Availability/common/FormDrawer/types';
import { getEnabledFormFields } from 'pages/Availability/common/FormDrawer/utils';
import { ReasonSelect } from 'pages/Availability/common/ReasonSelect';
import {
  getAssetFormValuesFromBaseAssetAvailabilityEntry,
  getExistingAssetAvailabilityFromBaseAssetAvailabilityEntry,
  getLatestAssetLimit,
  PREVIEW_ENTRY_ID,
} from 'pages/Availability/common/utils';
import { DrawerFixed } from 'pages/Availability/SiteLevel/AvailabilityForm.styled';
import { buildAvailabilityFormRules } from 'pages/Availability/SiteLevel/buildAvailabilityFormRules';
import { BaseAssetAvailabilityEntry, Site } from 'pages/Availability/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'store';

const disableOpenEnded = true;

interface UseAssetAvailabilityFormDrawerProps {
  site: Site;
  client: ApolloClient;
  title: string;
  width: string;
  editEntry: BaseAssetAvailabilityEntry | null | undefined;
  onRefetch: () => void;
}

interface UseAssetAvailabilityFormDrawerReturn {
  FormDrawer: React.ReactNode;
  previewEntries: BaseAssetAvailabilityEntry[];
  handleFormClose: () => void;
}

export const useAssetAvailabilityFormDrawer = ({
  site,
  client,
  title,
  width,
  editEntry,
  onRefetch,
}: UseAssetAvailabilityFormDrawerProps): UseAssetAvailabilityFormDrawerReturn => {
  const {
    availabilityFormOpen,
    setAvailabilityFormOpen,
    persistedAssetAvailabilityFormData,
    resetPersistedAssetAvailabilityFormData,
    setPersistedAssetAvailabilityFormData,
    setAvailabilityDateRange,
    setAvailabilityUserAction,
    setAlert,
  } = useStore((state) => ({
    availabilityFormOpen: state.availabilityFormOpen,
    setAvailabilityFormOpen: state.setAvailabilityFormOpen,
    persistedAssetAvailabilityFormData:
      state.persistedAssetAvailabilityFormData,
    resetPersistedAssetAvailabilityFormData:
      state.resetPersistedAssetAvailabilityFormData,
    setPersistedAssetAvailabilityFormData:
      state.setPersistedAssetAvailabilityFormData,
    setAvailabilityDateRange: state.setAvailabilityDateRange,
    setAvailabilityUserAction: state.setAvailabilityUserAction,
    setAlert: state.setAlert,
  }));

  const [isLoading, setIsLoading] = useState(false);
  const [previewEntries, setPreviewEntries] = useState<
    BaseAssetAvailabilityEntry[]
  >([]);

  const assets = site?.assets ?? [];
  const isEditMode = !!editEntry;
  const persistedFormDataRef = useRef(persistedAssetAvailabilityFormData);
  persistedFormDataRef.current = persistedAssetAvailabilityFormData;

  // Compute initial form values
  const initialFormValues = useMemo<NewAssetAvailabilityEntry>(() => {
    const persistedFormData = persistedFormDataRef.current;
    const hasPersistedData =
      persistedFormData && Object.keys(persistedFormData).length > 0;

    if (!availabilityFormOpen) {
      return ASSET_FORM_INITIAL_VALUES;
    }

    // For edit mode, use original entry data if no persisted data
    if (isEditMode && editEntry && !hasPersistedData) {
      return getAssetFormValuesFromBaseAssetAvailabilityEntry(editEntry);
    }

    // Use persisted data if it exists
    if (hasPersistedData && persistedFormData) {
      return {
        ...persistedFormData,
        startImmediately: persistedFormData.startImmediately ?? false,
        isOpenEnded: persistedFormData.isOpenEnded ?? false,
      };
    }

    return ASSET_FORM_INITIAL_VALUES;
  }, [availabilityFormOpen, isEditMode, editEntry]);

  const enabledFields = useMemo(
    () => getEnabledFormFields(initialFormValues, isEditMode),
    [initialFormValues, isEditMode],
  );

  // Build asset operational limits map for validation
  const assetOperationalLimits: AssetOperationalLimits = useMemo(() => {
    return assets.reduce((acc, asset) => {
      const limit = getLatestAssetLimit(asset.assetLimits);
      if (limit) {
        acc[getSourceKey(asset)] = limit;
      }
      return acc;
    }, {} as AssetOperationalLimits);
  }, [assets]);

  const debouncedSetPersistedFormData = useRef(
    debounce(setPersistedAssetAvailabilityFormData, 500),
  ).current;

  const handleEmptyAvailabilityUserAction = useCallback(() => {
    setAvailabilityUserAction({
      siteAvailabilityEntry: null,
      assetAvailabilityEntry: null,
      actionType: null,
    });
  }, [setAvailabilityUserAction]);

  const handleSetPreview = useCallback(
    (previewData: BaseAssetAvailabilityEntry[]) => {
      setPreviewEntries(previewData);

      if (previewData.length > 0 && previewData[0].start) {
        setAvailabilityDateRange({
          start: getPreviewChartTime(previewData, 'start'),
          end: getPreviewChartTime(previewData, 'end'),
        });
      }
    },
    [setAvailabilityDateRange],
  );

  const handleFormClose = useCallback(() => {
    setAvailabilityFormOpen(false);
    setPreviewEntries([]);
    handleEmptyAvailabilityUserAction();
    resetPersistedAssetAvailabilityFormData();
  }, [
    setAvailabilityFormOpen,
    handleEmptyAvailabilityUserAction,
    resetPersistedAssetAvailabilityFormData,
  ]);

  const handleFormSuccess = useCallback(
    (values: NewAssetAvailabilityEntry, type: 'edit' | 'create') => {
      if (values.start && values.end) {
        setAvailabilityDateRange({
          start: values.start,
          end:
            type === 'create' && values.repeatDaily && values.repeatEndInclusive
              ? values.repeatEndInclusive
              : values.end,
        });
      }

      handleFormClose();
      onRefetch();
    },
    [setAvailabilityDateRange, handleFormClose, onRefetch],
  );

  const formikRef = useRef<ReturnType<
    typeof useFormik<NewAssetAvailabilityEntry>
  > | null>(null);

  const formik = useFormik<NewAssetAvailabilityEntry>({
    initialValues: initialFormValues,
    validationSchema: assetLevelFormValidationSchema(
      enabledFields,
      assetOperationalLimits,
    ),
    validateOnChange: false,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsLoading(true);

      let result: {
        success: boolean;
        message: string;
        hasChanges?: boolean;
        count?: number;
      };

      const type = isEditMode ? 'edit' : 'create';

      if (type === 'create') {
        result = await performNewAssetAvailabilityMutation(
          client,
          values,
          assets,
        );
      } else {
        const existingEntry =
          getExistingAssetAvailabilityFromBaseAssetAvailabilityEntry(
            editEntry!,
          );

        result = await performEditAssetAvailabilityMutation(
          client,
          values,
          existingEntry,
        );
      }

      if (result.success) {
        if (type === 'edit' && !result.hasChanges) {
          // Don't show alert for no changes
        } else {
          setAlert?.('success', result.message);
        }
        handleFormSuccess(values, type);
      } else {
        setAlert?.('error', result.message);
      }

      setIsLoading(false);
    },
  });

  formikRef.current = formik;

  const assetInputErrors = useMemo(
    () => (formik.errors.assetInputs ?? {}) as AssetInputsErrors,
    [formik.errors.assetInputs],
  );

  // Apply cross-field business rules on top of formik
  const { handlers, helper, fieldEnablement } = buildAvailabilityFormRules(
    formik as unknown as ReturnType<typeof useFormik<NewSiteAvailabilityEntry>>,
    enabledFields,
    { disableOpenEnded },
  );

  // Build preview entries when form values change
  useEffect(() => {
    if (formik.values.start && formik.values.selectedAssets.length > 0) {
      const newPreviewEntries: BaseAssetAvailabilityEntry[] =
        formik.values.selectedAssets.map((assetSourceKey) => {
          const asset = assets.find((a) => getSourceKey(a) === assetSourceKey);
          const inputs = formik.values.assetInputs[assetSourceKey];

          return {
            id: PREVIEW_ENTRY_ID,
            start: formik.values.start!,
            end: formik.values.end,
            assetSourceKey,
            assetName: asset?.name ?? '',
            maxExportPower: inputs?.maxExportPower
              ? +inputs.maxExportPower
              : null,
            maxImportPower: inputs?.maxImportPower
              ? +inputs.maxImportPower
              : null,
            energyCapacity: inputs?.energyCapacity
              ? +inputs.energyCapacity
              : null,
            availabilityReason: formik.values.availabilityReason,
            notes: formik.values.notes,
            isOpenEnded: formik.values.isOpenEnded,
            createdAt: new Date().toISOString(),
          };
        });

      handleSetPreview(newPreviewEntries);
    }
    debouncedSetPersistedFormData(formik.values);
  }, [formik.values, assets, handleSetPreview, debouncedSetPersistedFormData]);

  const handleAssetSelectionChange = (selectedAssets: string[]) => {
    formik.setFieldValue('selectedAssets', selectedAssets);

    // Initialize inputs for newly selected assets
    const newAssetInputs = { ...formik.values.assetInputs };
    for (const assetSourceKey of selectedAssets) {
      if (!newAssetInputs[assetSourceKey]) {
        newAssetInputs[assetSourceKey] = {
          maxExportPower: null,
          maxImportPower: null,
          energyCapacity: null,
        };
      }
    }

    for (const assetSourceKey of Object.keys(newAssetInputs)) {
      if (!selectedAssets.includes(assetSourceKey)) {
        delete newAssetInputs[assetSourceKey];
      }
    }

    formik.setFieldValue('assetInputs', newAssetInputs);
  };

  const handleAssetInputChange = useCallback(
    (assetSourceKey: string, inputs: Partial<PowerEnergyInput>) => {
      formikRef.current?.setFieldValue(
        'assetInputs',
        (currentAssetInputs: Record<string, PowerEnergyInput>) => ({
          ...currentAssetInputs,
          [assetSourceKey]: {
            ...currentAssetInputs[assetSourceKey],
            ...inputs,
          },
        }),
      );
    },
    [],
  );

  const handleApplyToAll = (inputs: PowerEnergyInput) => {
    const updatedAssetInputs = { ...formik.values.assetInputs };
    for (const assetSourceKey of formik.values.selectedAssets) {
      updatedAssetInputs[assetSourceKey] = {
        maxExportPower: inputs.maxExportPower,
        maxImportPower: inputs.maxImportPower,
        energyCapacity: inputs.energyCapacity,
      };
    }
    formik.setFieldValue('assetInputs', updatedAssetInputs);
  };

  const getActions = (isLoading: boolean): Action[] => {
    return [
      {
        label: 'Cancel',
        action: () => {
          formik.resetForm({
            values: ASSET_FORM_INITIAL_VALUES,
          });
          handleFormClose();
        },
        showActionButton: !isLoading,
        props: {
          variant: Variants.Outlined,
          sentiment: Sentiments.Primary,
        },
      },
      {
        label: 'Submit',
        action: () => formik.submitForm(),
        showActionButton: !isLoading,
        props: {
          variant: Variants.Filled,
          sentiment: Sentiments.Primary,
        },
      },
    ];
  };

  const FormDrawer = (
    <DrawerFixed
      open={availabilityFormOpen}
      onClose={handleFormClose}
      width={width}
      getContainer={false}
      header={{
        title: title,
        description: 'At asset level',
      }}
      sticky={true}
      mask={false}
      elevation={true}
      size={Sizes.Medium}
      actions={getActions(isLoading)}
      closeButton={true}
      zIndex={5}
    >
      <div
        style={{
          height: '100vh',
          padding: '5px',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          overflow: 'auto',
        }}
      >
        {isLoading ? (
          <Flexbox
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            style={{ height: '100%' }}
          >
            <Loading size={Sizes.XXLarge} />
          </Flexbox>
        ) : (
          <Flexbox flexDirection="column">
            <Flexbox flexDirection="row" columnGap="16px">
              <FormField
                label="Start"
                mandatory={true}
                bottomLeftHelper={helper('start')}
              >
                <DateTimeSPPicker
                  testId="reductionFormStart"
                  ISOStringUTCValue={formik.values.start ?? null}
                  onChange={handlers.start}
                  disableDate={!fieldEnablement.startDateEnabled}
                  disableTime={!fieldEnablement.startTimeEnabled}
                />
              </FormField>

              <FormField
                label="End"
                mandatory={true}
                bottomLeftHelper={helper('end')}
              >
                <DateTimeSPPicker
                  testId="reductionFormEnd"
                  ISOStringUTCValue={formik.values.end ?? null}
                  onChange={handlers.end}
                  disableDate={!fieldEnablement.endDateEnabled}
                  disableTime={!fieldEnablement.endTimeEnabled}
                />
              </FormField>
            </Flexbox>

            <Flexbox flexDirection="row" style={{ marginTop: '16px' }}>
              <div style={{ width: '33%' }}>
                <Toggle
                  data-testid="reductionFormStartImmediately"
                  label="From next SP"
                  checked={formik.values.startImmediately}
                  disabled={!fieldEnablement.startImmediatelyEnabled}
                  onChange={handlers.startImmediately}
                />
              </div>
              <div style={{ width: '33%' }}>
                <Toggle
                  data-testid="reductionFormRepeatDaily"
                  label="Repeat daily"
                  checked={formik.values.repeatDaily}
                  disabled={!fieldEnablement.repeatDailyEnabled}
                  onChange={handlers.repeatDaily}
                />
              </div>
              <div style={{ width: '33%' }}>
                <Toggle
                  data-testid="reductionFormOpenEnded"
                  label="Open ended"
                  disabled={!fieldEnablement.openEndedEnabled}
                  checked={formik.values.isOpenEnded}
                  onChange={handlers.isOpenEnded}
                />
              </div>
            </Flexbox>

            {formik.values.repeatDaily && (
              <Flexbox flexDirection="row" style={{ marginTop: '36px' }}>
                <FormField
                  label="Repeat Until (Inclusive)"
                  mandatory={true}
                  bottomLeftHelper={helper('repeatEndInclusive')}
                >
                  <SingleDatePicker
                    data-testid="reductionFormRepeatEndInclusive"
                    placeholder="DD MM YYYY"
                    value={
                      formik.values.repeatEndInclusive
                        ? dayjs(formik.values.repeatEndInclusive)
                        : null
                    }
                    onChange={(value) => {
                      if (value) {
                        handlers.repeatEndInclusive(
                          value.startOf('day').toISOString(),
                        );
                      }
                    }}
                    disabled={!fieldEnablement.repeatEndInclusiveEnabled}
                  />
                </FormField>
              </Flexbox>
            )}

            <Flexbox flexDirection="column" style={{ marginTop: '36px' }}>
              <FormField
                label="Reason"
                mandatory={true}
                bottomLeftHelper={helper('availabilityReason')}
              >
                <ReasonSelect
                  value={formik.values.availabilityReason}
                  disabled={!fieldEnablement.availabilityReasonEnabled}
                  onChange={handlers.availabilityReason}
                  availabilityControl={SiteAvailabilityControl.AssetLevel}
                />
              </FormField>

              <FormField mandatory={false}>
                <TextArea
                  disabled={!fieldEnablement.notesEnabled}
                  data-testid="reductionFormComment"
                  placeholder="Additional notes (optional)"
                  resize="vertical"
                  value={formik.values.notes ?? ''}
                  onChange={(e) => handlers.notes(e.target.value)}
                />
              </FormField>
            </Flexbox>

            <div style={{ marginTop: '16px' }}>
              <Divider size={Sizes.Small} prominence={'subtle'} />
            </div>

            <Flexbox flexDirection="column" style={{ marginTop: '16px' }}>
              <FormField
                label="Assets"
                mandatory={true}
                bottomLeftHelper={
                  formik.errors.selectedAssets && formik.touched.selectedAssets
                    ? {
                        content: formik.errors.selectedAssets as string,
                        sentiment: Sentiments.Negative,
                      }
                    : undefined
                }
              >
                <AssetSelector
                  assets={assets}
                  selectedAssets={formik.values.selectedAssets}
                  onChange={handleAssetSelectionChange}
                  disabled={isEditMode}
                />
              </FormField>
            </Flexbox>

            {formik.values.selectedAssets.length > 0 && (
              <AssetLimitsTable
                assets={assets}
                selectedAssets={formik.values.selectedAssets}
                assetInputs={formik.values.assetInputs}
                onChange={handleAssetInputChange}
                onApplyToAll={handleApplyToAll}
                disabled={
                  !fieldEnablement.maxExportPowerEnabled ||
                  !fieldEnablement.maxImportPowerEnabled ||
                  !fieldEnablement.energyCapacityEnabled
                }
                errors={assetInputErrors}
              />
            )}
          </Flexbox>
        )}
      </div>
    </DrawerFixed>
  );

  return {
    FormDrawer,
    previewEntries,
    handleFormClose,
  };
};
